import { useCallback, useRef } from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import { useMobileLocalizationContext } from './MobileI18nProvider';

/**
 * Minimal arb-tools bundle surface we depend on. The real type from
 * `@amzn/arb-tools` carries more, but we only call these two methods
 * and keep the typing local so the rest of the app doesn't depend on
 * the upstream symbol.
 */
interface ArbBundle {
  getMessage: (id: string) => string;
  formatMessage: (id: string, args: Record<string, unknown>) => string;
}

export interface Messages<K extends string> {
  /** Look up a translated string by typed key. */
  readonly t: (key: K) => string;
  /**
   * Look up a translated string with ICU placeholder substitution.
   * Use for strings like `'Choose locations ({count})'` instead of
   * hand-rolled `string.replace('{count}', ...)`.
   */
  readonly f: (key: K, args: Record<string, unknown>) => string;
  /** True while the bundle is being fetched on first render. */
  readonly isLoading: boolean;
}

/**
 * Generic typed accessor for an arb-tools message bundle.
 *
 * - `K` is the union of allowed keys for the bundle — keep it in
 *   lockstep with the puff.json `resources` keys to get a compile-time
 *   error on a typoed key.
 * - `t(key)` and `f(key, args)` are referentially stable across
 *   renders. Internally they read from a ref that always points at the
 *   latest bundle, which means consumers can safely put them in
 *   `useMemo`/`useCallback` deps without re-creating their memos every
 *   render. Stencil components like `Select` rely on stable `value`
 *   references; without this guarantee, options whose labels come from
 *   `t(...)` would create a new selected option on every render and
 *   the trigger would render the placeholder instead of the selected
 *   label.
 * - During the first paint window, before the bundle resolves,
 *   `t(key)` falls back to the key and `f(key, args)` falls back to
 *   the key as well. This matches the test mock and avoids a crash.
 *
 * @example
 *   type NavKey = 'auditsLink' | 'tasksLink';
 *   const { t } = useMessages<NavKey>('mobile.translations.nav');
 *   <Link>{t('auditsLink')}</Link>
 *
 * @example  // ICU substitution
 *   <Text>{f('locationsLinkSelected', { count: form.locations.length })}</Text>
 */
export function useMessages<K extends string>(bundleName: string): Messages<K> {
  // Thread the shell's LocalizationContext through as the second arg
  // so `useBundle` places it in the effect dep array and passes it into
  // `mbm.getBundle(bundle, context)`. arb-tools' getBundle prefers the
  // explicit context over its baked-in `localizationContextProvider`,
  // so bundles refetch when the user's language preference changes and
  // messages resolve for the runtime locale, not the mbm's default.
  const localizationContext = useMobileLocalizationContext();

  // useBundle's d.ts lies about the bundle being non-null on first render.
  // Cast to the truthful shape and fall back to the key when undefined.
  const [bundle, isLoading] = useBundle(bundleName, localizationContext) as [
    ArbBundle | undefined,
    boolean,
  ];

  // Sync the ref synchronously during render — not in `useEffect` —
  // because `t`/`f` are stable callbacks that read `bundleRef.current`
  // during the same render. An effect-based sync runs after paint, so
  // any `t(key)` call in the render that first receives the new bundle
  // would still see the stale ref and pin the previous locale's text in
  // the DOM until something else forces a re-render.
  const bundleRef = useRef<ArbBundle | undefined>(bundle);
  bundleRef.current = bundle;

  const t = useCallback<(key: K) => string>(
    (key) => bundleRef.current?.getMessage(key) ?? key,
    [],
  );
  const f = useCallback<(key: K, args: Record<string, unknown>) => string>(
    (key, args) => bundleRef.current?.formatMessage(key, args) ?? key,
    [],
  );

  return { t, f, isLoading };
}
